import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/i18n";
import { articles } from "../data/articles";
import { useSeo } from "../hooks/useSeo";

export default function BlogList() {
  const { lang, t } = useI18n();

  useSeo({
    title: lang === "de" ? "Blog — Azure-Architektur & Landing Zones" : "Blog — Azure architecture & landing zones",
    description:
      lang === "de"
        ? "Field Notes zu Azure-Architektur, Landing Zones und Platform Engineering von Cloudship."
        : "Field notes on Azure architecture, landing zones and platform engineering by Cloudship.",
    path: "/blog",
    lang,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const fmt = (d) =>
    new Date(d).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

  return (
    <main data-testid="blog-list-page" className="pt-32 pb-24">
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <p className="cs-overline mb-4">{t("blog.overline")}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight max-w-3xl">
          {t("blog.title")}
        </h1>
        <p className="text-slate-400 mt-5 max-w-2xl text-base lg:text-lg">{t("blog.subtitle")}</p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((a, i) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              data-testid={`blog-list-card-${i}`}
              className="cs-card group overflow-hidden flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={a.cover}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col gap-3">
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="text-sky-400 font-semibold uppercase tracking-[0.18em] text-[10px]">
                    {a.tag[lang]}
                  </span>
                  <span>·</span>
                  <span>{fmt(a.date)}</span>
                  <span>·</span>
                  <span>{a.readingTime} {t("blog.reading_time")}</span>
                </div>
                <h3 className="font-display text-lg text-white leading-snug group-hover:text-sky-300 transition-colors">
                  {a.title[lang]}
                </h3>
                <p className="text-sm text-slate-400 line-clamp-3">{a.excerpt[lang]}</p>
                <span className="text-sm text-sky-400 mt-2 inline-flex items-center gap-1">
                  {t("blog.read_more")} <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
